import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail, CONTACT_EMAIL, COMPANY } from "@/lib/api";

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: params.get("subject") || "General inquiry", message: "", website: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending"); setError("");
    try {
      await api.post("/contact", form);
      setState("sent");
      toast.success("Message sent. We'll be in touch soon.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setState("idle");
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact Us" description="Contact Explore HOA's with questions, corrections or partnership inquiries." path="/contact" />
      <PageHeader eyebrow="Contact" title="Get in touch" subtitle="Questions, corrections, accessibility requests or partnership ideas: we read every message." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 grid gap-6 sm:gap-10 lg:grid-cols-[1fr_360px]">
        {state === "sent" ? (
          <div className="rounded-2xl border border-[#63D8C4] bg-[#E6F4F1] p-10 text-center" data-testid="contact-success">
            <CheckCircle2 className="h-12 w-12 text-[#03A896] mx-auto" />
            <h2 className="mt-4 text-2xl font-semibold text-[#0B2150]">Thank you!</h2>
            <p className="mt-2 text-[#334155]">Your message was received. We'll reply to {form.email}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-8 space-y-5" data-testid="contact-form" noValidate={false}>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-1.5"><Label htmlFor="c-name">Name *</Label><Input id="c-name" required value={form.name} onChange={set("name")} data-testid="contact-name-input" /></div>
              <div className="space-y-1.5"><Label htmlFor="c-email">Email *</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" /></div>
              <div className="space-y-1.5"><Label htmlFor="c-phone">Phone</Label><Input id="c-phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" /></div>
              <div className="space-y-1.5"><Label htmlFor="c-subject">Subject</Label><Input id="c-subject" value={form.subject} onChange={set("subject")} data-testid="contact-subject-input" /></div>
            </div>
            <div className="space-y-1.5"><Label htmlFor="c-msg">Message *</Label><Textarea id="c-msg" required minLength={5} rows={6} value={form.message} onChange={set("message")} data-testid="contact-message-input" /></div>
            <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
            {error && <p role="alert" className="text-sm text-red-700" data-testid="contact-error">{error}</p>}
            <button type="submit" disabled={state === "sending"} data-testid="contact-submit-btn"
              className="rounded-full bg-[#0B2150] text-white px-8 py-3 font-semibold hover:bg-[#03A896] transition-colors disabled:opacity-60">
              {state === "sending" ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
        <aside className="rounded-2xl bg-[#0B2150] text-white p-6 sm:p-8 h-fit">
          <Mail className="h-8 w-8 text-[#63D8C4]" />
          <h2 className="mt-4 text-xl font-semibold">Email us directly</h2>
          <a href={`mailto:${CONTACT_EMAIL}`} className="mt-2 block text-[#63D8C4] break-all hover:text-white" data-testid="contact-direct-email">{CONTACT_EMAIL}</a>
          <p className="mt-6 text-sm text-slate-300">Explore HOA's is operated by {COMPANY}. For accessibility help, include the page URL, a description of the issue and your preferred contact method.</p>
        </aside>
      </div>
    </div>
  );
}
